import React, { useState } from "react";
import spaceNeedle from "./Space Needle.jpg";
import UEHowToPlay from "./UEHowToPlay";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./App";
import UEContestHome from "./UEContestHome";
import UEContestDetail from "./UEContestDetail";
import UEAdminPanel from "./UEAdminPanel";

const UE_ADMIN_UID = "fy1HRZav9TW5MJFxHE1hc3mJFUl2"; // same admin as WC Pool

function FootballIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
      <ellipse cx="50" cy="50" rx="44" ry="28" fill="#8B4513" stroke="#5D2E0C" strokeWidth="3"/>
      <line x1="6" y1="50" x2="94" y2="50" stroke="white" strokeWidth="3"/>
      <line x1="50" y1="22" x2="50" y2="78" stroke="white" strokeWidth="3"/>
      <line x1="35" y1="30" x2="35" y2="70" stroke="white" strokeWidth="2"/>
      <line x1="65" y1="30" x2="65" y2="70" stroke="white" strokeWidth="2"/>
    </svg>
  );
}

function UEHeader({ activeContestName, onLeaveContest, onHowToPlay }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, displayName, logout } = useAuth();
  const inContest = location.pathname.includes("/underdog-edge/contest/");

  return (
    <header style={{
      background: "linear-gradient(135deg, #0a1220 0%, #111a2e 50%, #0a1220 100%)",
      borderBottom: "3px solid #FF6B35",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Hash mark pattern */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.035,
        backgroundImage: "repeating-linear-gradient(90deg, white 0px, white 2px, transparent 2px, transparent 40px), repeating-linear-gradient(0deg, white 0px, white 1px, transparent 1px, transparent 20px)",
        pointerEvents: "none",
      }} />

      {/* Top stripe */}
      <div style={{ height: 4, background: "linear-gradient(90deg, #FF6B35, #ff9a6c, #FF6B35, #c94d1f)" }} />

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "6px 12px" }}>
        {/* Main row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0, display: "flex", alignItems: "center" }} title="Home">
            <img src={spaceNeedle} alt="Home" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }} />
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
            <FootballIcon size={28} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(14px, 4vw, 18px)", letterSpacing: 2, color: "white", lineHeight: 1, whiteSpace: "nowrap" }}>
                UNDERDOG <span style={{ color: "#FF6B35" }}>EDGE</span>
              </div>
              {inContest && activeContestName && (
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {activeContestName}
                </div>
              )}
            </div>
          </div>

          {/* Desktop controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} className="hide-mobile">
            {user && (
              <>
                <button onClick={onHowToPlay} className="btn btn-ghost btn-sm">❓ How to Play</button>
                <button onClick={() => navigate("/account")} className="btn btn-ghost btn-sm">👤 {displayName}</button>
                <button onClick={logout} className="btn btn-ghost btn-sm">Sign Out</button>
              </>
            )}
          </div>

          {/* Mobile: account icon only in main row */}
          <div className="hide-desktop" style={{ flexShrink: 0 }}>
            {user && <button onClick={() => navigate("/account")} className="btn btn-ghost btn-sm">👤</button>}
          </div>
        </div>

        {/* Mobile: How to Play stacked on second row */}
        {user && (
          <div className="hide-desktop" style={{ paddingTop: 4, paddingLeft: 44 }}>
            <button onClick={onHowToPlay} className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "3px 10px" }}>
              ❓ How to Play
            </button>
          </div>
        )}
      </div>

      {/* Bottom stripe */}
      <div style={{ height: 3, background: "linear-gradient(90deg, #c94d1f, #FF6B35, #ff9a6c, #c94d1f)" }} />
    </header>
  );
}

export default function UEApp() {
  const { user } = useAuth();
  const [activeContestName, setActiveContestName] = useState(null);
  const [onLeaveContest, setOnLeaveContest] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  return (
    <div style={{ minHeight: "100vh" }}>
      {showHowToPlay && <UEHowToPlay onClose={() => setShowHowToPlay(false)} />}
      <UEHeader activeContestName={activeContestName} onLeaveContest={onLeaveContest} onHowToPlay={() => setShowHowToPlay(true)} />

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px 16px" }}>
        <Routes>
          <Route path="" element={<UEContestHome />} />
          <Route path="contest/:contestId" element={
            <UEContestDetail
              onMount={(name, leaveFn) => {
                setActiveContestName(name);
                setOnLeaveContest(() => leaveFn);
              }}
              onLeave={() => {
                setActiveContestName(null);
                setOnLeaveContest(null);
              }}
            />
          } />
        </Routes>

        {/* Admin panel — rendered below on any UE screen if admin */}
        {user?.uid === UE_ADMIN_UID && <UEAdminPanel />}
      </div>
    </div>
  );
}
